import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Grid,
  TextField,
  Typography,
  Box,
  IconButton,
  Snackbar,
  Chip,
} from "@mui/material";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SendIcon from "@mui/icons-material/Send";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { z } from "zod";
import { AxiosError } from "axios";
import {
  Copywriter,
  inviteCopywriter,
  disableCopywriter,
  enableCopywriter,
  resendCopywriterInvite,
} from "../../api/client";

// Validation schema for copywriter form
const copywriterValidationSchema = z.object({
  email: z.string().min(1, "Email required").email("Invalid email"),
  name: z.string().min(1, "Name is required"),
  surname: z.string().min(1, "Surname is required"),
});

interface CopywriterClientDialogProps {
  open: boolean;
  copywriter: Copywriter | null;
  onClose: (refresh: boolean) => void;
  onDisableEnable?: (copywriter: Copywriter, newDisabledState: boolean) => void;
  organizationId: string;
}

const initCopywriterState: Partial<Copywriter> = {
  email: "",
  name: "",
  surname: "",
};

/**
 * Dialog component for inviting and editing client users
 * Used in the organization detail view by admins
 */
export const CopywriterClientDialog: React.FC<CopywriterClientDialogProps> = ({
  open,
  copywriter,
  onClose,
  onDisableEnable,
  organizationId,
}) => {
  const [editingCopywriter, setEditingCopywriter] =
    useState<Partial<Copywriter>>(initCopywriterState);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [showResendDialog, setShowResendDialog] = useState(false);
  const [resendCredentials, setResendCredentials] = useState<{
    email: string;
    password: string;
  } | null>(null);
  const [copySnackbar, setCopySnackbar] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showDisableEnableConfirm, setShowDisableEnableConfirm] =
    useState(false);

  // Update local state when the copywriter prop changes
  useEffect(() => {
    if (copywriter) {
      setEditingCopywriter({ ...copywriter });
    } else {
      setEditingCopywriter(initCopywriterState);
    }
    setValidationErrors({});
  }, [copywriter, open]);

  // Handler for saving the copywriter (invite only - editing name/surname not supported)
  const handleSave = async () => {
    // Validate form data
    const validationResult = copywriterValidationSchema.safeParse({
      email: editingCopywriter.email,
      name: editingCopywriter.name,
      surname: editingCopywriter.surname,
    });

    if (!validationResult.success) {
      // Extract errors from zod
      const errors: Record<string, string> = {};
      validationResult.error.errors.forEach((err) => {
        if (err.path[0]) {
          errors[err.path[0] as string] = err.message;
        }
      });
      setValidationErrors(errors);
      return;
    }

    // Clear validation errors
    setValidationErrors({});

    try {
      setIsSaving(true);
      // Only invite new copywriters - editing not supported from admin
      await inviteCopywriter({
        email: editingCopywriter.email || "",
        name: editingCopywriter.name || "",
        surname: editingCopywriter.surname || "",
        organization_id: organizationId,
      });

      setEditingCopywriter(initCopywriterState);
      onClose(true);
    } catch (error) {
      // Extract error message from backend response
      let errorMessage = "An error occurred";

      if (error instanceof AxiosError && error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      // Show error on email field
      setValidationErrors({
        email: errorMessage,
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Update a specific field in the copywriter object
  const handleFieldChange = (
    field: keyof Copywriter,
    value: string | boolean | undefined
  ) => {
    setEditingCopywriter({ ...editingCopywriter, [field]: value });
    // Clear validation error for this field when user changes it
    if (validationErrors[field]) {
      setValidationErrors({ ...validationErrors, [field]: "" });
    }
  };

  // Handle resending invite
  const handleResendInvite = async () => {
    if (!editingCopywriter?.id) return;

    try {
      const response = await resendCopywriterInvite(editingCopywriter.id);
      const data = response.data.data;

      setResendCredentials({
        email: data.email,
        password: data.temporary_password,
      });
      setShowResendDialog(true);
    } catch (error) {
      console.error("Failed to resend invite:", error);
      let errorMessage = "Failed to resend invitation. Please try again.";
      if (error instanceof AxiosError && error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      alert(errorMessage);
    }
  };

  // Handle copy to clipboard
  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopySnackbar(true);
  };

  // Handle disable/enable
  const handleDisableEnable = () => {
    setShowDisableEnableConfirm(true);
  };

  const confirmDisableEnable = async () => {
    if (!copywriter?.id) return;

    const action = copywriter.disabled ? "enable" : "disable";

    try {
      if (copywriter.disabled) {
        await enableCopywriter(copywriter.id);
      } else {
        await disableCopywriter(copywriter.id);
      }

      // Notify parent to refresh
      if (onDisableEnable) {
        onDisableEnable(copywriter, !copywriter.disabled);
      }
      setShowDisableEnableConfirm(false);
      onClose(true);
    } catch (error) {
      console.error(`Failed to ${action} copywriter:`, error);
      setValidationErrors({
        general: `Failed to ${action} client user. Please try again.`,
      });
      setShowDisableEnableConfirm(false);
    }
  };

  const isEditMode = !!editingCopywriter?.id;

  return (
    <>
      <Dialog
        open={open}
        onClose={() => {
          onClose(false);
          setEditingCopywriter(initCopywriterState);
          setValidationErrors({});
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>
              {isEditMode ? `Edit Client User` : "Invite Client User"}
            </span>
            {isEditMode && copywriter?.disabled && (
              <Chip label="Disabled" size="small" color="error" />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Email field */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                value={editingCopywriter?.email || ""}
                onChange={(e) => handleFieldChange("email", e.target.value)}
                disabled={isEditMode}
                error={!!validationErrors.email}
                helperText={
                  validationErrors.email ||
                  (isEditMode ? "Email cannot be changed" : "")
                }
              />
            </Grid>

            {/* Name and Surname */}
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Name"
                value={editingCopywriter?.name || ""}
                onChange={(e) => handleFieldChange("name", e.target.value)}
                disabled={isEditMode}
                error={!!validationErrors.name}
                helperText={validationErrors.name || ""}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Surname"
                value={editingCopywriter?.surname || ""}
                onChange={(e) => handleFieldChange("surname", e.target.value)}
                disabled={isEditMode}
                error={!!validationErrors.surname}
                helperText={validationErrors.surname || ""}
              />
            </Grid>

            {/* Status info for edit mode */}
            {isEditMode && (
              <Grid item xs={12}>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    Status:
                  </Typography>
                  <Chip
                    label={copywriter?.status || "Unknown"}
                    size="small"
                    color={
                      copywriter?.status === "Joined" ? "success" : "warning"
                    }
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", gap: 1 }}>
            {isEditMode &&
              !copywriter?.email_confirmed &&
              !copywriter?.disabled && (
                <Button
                  onClick={handleResendInvite}
                  color="primary"
                  startIcon={<SendIcon />}
                >
                  Resend Invite
                </Button>
              )}
            {isEditMode && onDisableEnable && copywriter && (
              <Button
                onClick={handleDisableEnable}
                color={copywriter.disabled ? "success" : "error"}
                startIcon={
                  copywriter.disabled ? <CheckCircleIcon /> : <BlockIcon />
                }
              >
                {copywriter.disabled ? "Enable" : "Disable"}
              </Button>
            )}
          </Box>
          <Box>
            <Button onClick={() => onClose(false)}>Cancel</Button>
            {!isEditMode && (
              <Button
                onClick={handleSave}
                variant="contained"
                sx={{ ml: 1 }}
                disabled={isSaving}
              >
                {isSaving ? "Inviting..." : "Invite"}
              </Button>
            )}
          </Box>
        </DialogActions>

        {/* Resend Credentials Dialog */}
        <Dialog
          open={showResendDialog}
          onClose={() => setShowResendDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Invitation Resent</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              In case of problems with the email, you can copy and paste the
              credentials
            </Typography>

            <Box
              sx={{
                position: "relative",
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                backgroundColor: "#f5f5f5",
                p: 2,
                pr: 6,
              }}
            >
              <Typography
                variant="body2"
                component="pre"
                sx={{ m: 0, fontFamily: "monospace" }}
              >
                {`Name: ${copywriter?.name || ""} ${
                  copywriter?.surname || ""
                }\nEmail: ${resendCredentials?.email || ""}\nPassword: ${
                  resendCredentials?.password || ""
                }`}
              </Typography>
              <IconButton
                onClick={() =>
                  handleCopyToClipboard(
                    `Name: ${copywriter?.name || ""} ${
                      copywriter?.surname || ""
                    }\nEmail: ${resendCredentials?.email || ""}\nPassword: ${
                      resendCredentials?.password || ""
                    }`
                  )
                }
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                }}
              >
                <ContentCopyIcon />
              </IconButton>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowResendDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Copy snackbar */}
        <Snackbar
          open={copySnackbar}
          autoHideDuration={2000}
          onClose={() => setCopySnackbar(false)}
          message="Copied to clipboard"
        />
      </Dialog>

      {/* Disable/Enable Confirmation Dialog */}
      <Dialog
        open={showDisableEnableConfirm}
        onClose={() => setShowDisableEnableConfirm(false)}
      >
        <DialogTitle>
          {copywriter?.disabled ? "Enable Client User" : "Disable Client User"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {copywriter?.disabled
              ? "Are you sure you want to enable this client user?"
              : "Are you sure you want to disable this client user? They will not be able to log in."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDisableEnableConfirm(false)}>
            Cancel
          </Button>
          <Button
            onClick={confirmDisableEnable}
            color={copywriter?.disabled ? "success" : "error"}
            variant="contained"
          >
            {copywriter?.disabled ? "Enable" : "Disable"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
